import Icon, {
  DoubleRightOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { useBoolean } from 'ahooks';
import { Alert, Button, Card, Checkbox, Space, Table, TableColumnsType, Tooltip } from 'antd';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import { flow, findLastIndex, toNumber } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useAsync } from 'react-use';
import {
  AssistantPerfRes,
  getAssistantPerfReport,
  getAssistantPerfReportSum,
  getAssistantPerfReportAvg,
  PerfAmount,
} from 'services/api/dashboard';
import Fold from 'views/Conversation/Sider/Fold';
import {
  momentDateRange2FetchParams,
  numDisplayWithFixed2,
  numDisplay,
} from 'views/Dashboard/ImplementData/utils';
import ColSelector from './ColSelector';
import { useFilterCtx } from './Filter';
import PerfRuleSelectorModal from './PerfRuleSelectorModal';
import {
  amountColumn,
  columns as _tableCol,
  emptyDataDisplay,
  emptyDataMap,
  persistColumn,
  Column,
  cannotSortSelfColumn,
  skintificColumns,
} from './tableCol';
import { useAuthorization } from 'components/Authorization';
import { useMemoizedFn } from 'ahooks';
import { css } from '@emotion/css';
import PerfSort from './PerfSort';
import { ReactComponent as SvgSort } from '../../assets/sort.svg';
import { useTransAppProductName } from 'hooks/useTrans';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import ExportPrefModal from './ExportPrefModal';
import ExportAssistantModal from './ExportAssistantModal';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

const cacheKey = 'fett/dashboard-perf-show-cols-v2';
const sortCachekey = 'fett/dashboard-perf-sort-cols-v2';

const toFixedAvgValue = (avgSummaryValue?: AssistantPerfRes) => {
  if (avgSummaryValue) {
    const keys = Object.keys(avgSummaryValue);
    keys.forEach((key) => {
      let value = avgSummaryValue[key];
      if (typeof value === 'number') {
        value = toNumber(value.toFixed(4));
        (avgSummaryValue[key] as number) = value;
      }
    });
  }
  return avgSummaryValue;
};

const getSortInfo = (): Record<Column['key'], number> => {
  const sortCache = localStorage.getItem(sortCachekey);
  if (!sortCache) {
    return {} as Record<Column['key'], number>;
  }
  try {
    return JSON.parse(sortCache);
  } catch (_) {
    return {} as Record<Column['key'], number>;
  }
};

const getShowCols = (tableCol: Column[]) => {
  const cache = localStorage.getItem(cacheKey);
  const sortInfo = getSortInfo();
  if (!cache) {
    return tableCol;
  }
  const cacheSetting: string[] = JSON.parse(cache);
  return tableCol.map((x) => {
    const persist = persistColumn.includes(x.key);
    return {
      ...x,
      show: persist || cacheSetting.includes(x.key),
      sortIndex: sortInfo[x.key],
    };
  });
};

const PerfTable = (props: {
  adminId?: string;
  rangeSelect?: JSX.Element;
  departmentSelect?: JSX.Element;
  customSelect?: JSX.Element;
}) => {
  useEffect(() => {
    posthog.capture('$assistantPerformance', {
      type: 'entryPage',
    });
  }, []);
  const { whiteListConfig: { SKINTIFICPERF } = {} } = useOrgConfigStateValue();
  const tableCol = useMemo(() => {
    return SKINTIFICPERF ? [..._tableCol, ...skintificColumns] : _tableCol;
  }, [SKINTIFICPERF]);

  const fixedColumns = useMemo(() => tableCol.filter((column) => !column.group), [tableCol]);

  const { adminId, rangeSelect, customSelect, departmentSelect } = props;

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const [isfold, setIsfold] = useState(true);

  const [meta, setMeta] = useState(() => getShowCols(tableCol));

  const [exportModalVisible, { setTrue: showExportModal, setFalse: hideExportModal }] =
    useBoolean();
  const {
    meta: assistantIds,
    show: showExportAssistantModal,
    hide: closeExportAssistantModal,
    visible: exportAssistantModalVisible,
  } = useModal<number[]>();

  const selectedColumns = useMemo(() => {
    return meta.filter((column) => column.show);
  }, [meta]);

  useEffect(() => {
    const showColKeys = meta.filter((x) => x.show).map((x) => x.key);
    localStorage.setItem(cacheKey, JSON.stringify(showColKeys));

    const sortInfo = meta.reduce<Record<Column['key'], number>>((res, cur) => {
      if (cur.sortIndex !== undefined) {
        res[cur.key] = cur.sortIndex;
      }
      return res;
    }, {} as Record<Column['key'], number>);
    localStorage.setItem(sortCachekey, JSON.stringify(sortInfo));
  }, [meta]);

  const getPermission = useGetPermission();

  const exportable = getPermission('绩效报告导出');
  const groupPermission = getPermission('绩效报告-查看部门绩效');

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { id } = payload || {};

  const { show: showRule, hide: hideRule, visible: ruleVisible } = useModal(false);
  const {
    value: filterCtx,
    //  onChange
  } = useFilterCtx();
  const { departments } = filterCtx;

  const params = useMemo(() => {
    const { dateRange, conversationType, departments, ...rest } = filterCtx;
    return {
      // 产品在提测以后加的需求，上线就被老板砍了
      conversationType: 2 as const,
      departmentIds: departments?.map((_) => _.id),
      ...rest,
      ...momentDateRange2FetchParams(dateRange),
    };
  }, [filterCtx]);

  const { value } = useAsync<() => Promise<AssistantPerfRes[]>>(
    () => getAssistantPerfReport(params).then((res) => res.assistantPerformances),
    [params],
  );

  const { value: summaryValue } = useAsync<() => Promise<Array<AssistantPerfRes>>>(() => {
    return Promise.all([
      getAssistantPerfReportSum(params).then((res) => res.assistantPerformances?.[0]),
      getAssistantPerfReportAvg(params).then((res) => res.assistantPerformances?.[0]),
    ]);
  }, [params]);

  const { currency } = useDashboardCurrency();

  const dataSource = useMemo(() => {
    return value?.filter((x) => {
      if (!groupPermission && x.assistantId !== id) {
        return false;
      }

      return String(x.assistantId) !== adminId;
    });
  }, [value, adminId, groupPermission, id]);

  const sortedColumns = useMemo(() => {
    // 需要显示的column
    const showColumns = meta.filter((x) => x.show);
    // 固定显示的column, 其实等同于 persistColumn, 考虑到可能修改persistColumn 的show属性，这里再过滤下
    const persistColumns: Column[] = [];
    const unPersistColumns: Column[] = [];
    showColumns.forEach((x) => {
      if (cannotSortSelfColumn.includes(x.key)) {
        persistColumns.push(x);
      } else {
        unPersistColumns.push(x);
      }
    });
    // 手动排序过的column
    const sortedColumns: Column[] = [];
    // 未手动排序过的column
    const unsortedColumns: Column[] = [];
    unPersistColumns.forEach((x) => {
      if (x.sortIndex !== undefined) {
        sortedColumns.push(x);
      } else {
        unsortedColumns.push(x);
      }
    });
    // 对未手动排序过的column按index排序
    const sortedUnsortedColumns = unsortedColumns.sort((a, b) => a.index - b.index);
    // 如果都没参与过手动排序，则按原顺序排列
    if (sortedColumns.length === 0) {
      return [...persistColumns, ...sortedUnsortedColumns];
    } else {
      // 参与过手动排序，固定的column永远再最前面，手动排序过的放前面，其他的按原有的顺序排列
      return [
        ...persistColumns,
        ...sortedColumns.sort((a, b) => a.sortIndex! - b.sortIndex!),
        ...sortedUnsortedColumns,
      ];
    }
  }, [meta]);

  const renderCell = useCallback(
    (originRender?: Column['render']) => {
      if (typeof originRender === 'undefined') {
        return flow(emptyDataMap, emptyDataDisplay);
      }
      if (typeof originRender === 'function') {
        return flow(emptyDataMap, originRender);
      }

      const { type } = originRender;

      if (type === 'amount') {
        return (value: PerfAmount) => originRender.render(value, currency?.shorthand);
      }

      return undefined;
    },
    [currency?.shorthand],
  );

  const columns = useMemo<TableColumnsType<AssistantPerfRes>>(() => {
    const dataColumns = sortedColumns.map((item) => {
      const { key, dataIndex, tooltip, render: originRender, width = 120 } = item;

      const sorter = persistColumn.includes(key)
        ? {}
        : {
            sorter: (a: any, b: any) => {
              if (typeof a[dataIndex] !== 'object') {
                return a[dataIndex] - b[dataIndex];
              }

              return (
                a[dataIndex]?.currencyToAmount?.RMB?.amount -
                b[dataIndex]?.currencyToAmount?.RMB?.amount
              );
            },
          };

      const render = renderCell(originRender);

      return {
        title: () => {
          const unit = amountColumn.includes(key) ? `(${currency?.symbol})` : '';
          return (
            <ColTitle width={width}>
              {appProductNameT(`ViewDashboard.${key}`)}
              {unit}
              {tooltip ? (
                <Tooltip title={appProductNameT(`ViewDashboard.${tooltip}`)}>
                  <QuestionCircleOutlined />
                </Tooltip>
              ) : null}
            </ColTitle>
          );
        },
        className: `head-col ${dataIndex}`,
        ...sorter,
        ...item,
        render,
        // 排序图标的宽度  table scroll y 导致，故该宽度需要额外计算
        // @example  scroll={{ x: 'max-content', y: 550 }}
        width: width + 32,
        dataIndex,
      };
    });

    const operation = {
      key: 'operation',
      title: t('操作'),
      fixed: 'right',
      width: 160,
      render: (_: any, record: AssistantPerfRes) => {
        const { assistantId } = record;

        return (
          <Button
            type="link"
            onClick={() => {
              showExportAssistantModal([assistantId]);
            }}
          >
            {t('export_pref_modal_export_assistant_data')}
          </Button>
        );
      },
    } as const;

    return [...dataColumns, operation];
  }, [sortedColumns, t, renderCell, currency?.symbol, appProductNameT, showExportAssistantModal]);

  // 统计行
  const renderTableSummary = useMemoizedFn(() => {
    const [sumSummaryValue, avgSummaryValue] = summaryValue ?? [];
    const fixedColIndex = findLastIndex(columns, (col) => col.fixed === 'left');
    const renderRow = (title: string, summaryData: AssistantPerfRes, isAvgRow = false) => {
      return (
        <Table.Summary.Row>
          <Table.Summary.Cell key="select" className={tableSummaryBg} index={0} />
          {columns.map((column, index) => {
            if (index <= fixedColIndex) {
              if (index === 0) {
                return (
                  <Table.Summary.Cell
                    key={column.key}
                    className={tableSummaryBg}
                    index={index + 1}
                    colSpan={fixedColIndex + 1}
                  >
                    {title}
                  </Table.Summary.Cell>
                );
              } else {
                return null;
              }
            } else {
              // @ts-ignore
              const columnValue = summaryData?.[column.dataIndex];

              // 平均值行：对于使用 numDisplay 渲染的数量列，改用保留两位小数的格式
              const customerRender =
                column.render ??
                function (val) {
                  return val;
                };
              let content = '';
              // @ts-ignore
              if (column.dataIndex) {
                if (isAvgRow) {
                  // 从原始列定义中获取 render 函数，用于判断是否是 numDisplay
                  const originalColumn = sortedColumns.find(
                    // @ts-ignore
                    (col) => col.dataIndex === column.dataIndex,
                  );
                  const originalRender = originalColumn?.render;
                  const isNumDisplayRender =
                    typeof originalRender === 'function' && originalRender === numDisplay;
                  if (isNumDisplayRender) {
                    content = numDisplayWithFixed2(columnValue);
                  } else {
                    // @ts-ignore
                    content = customerRender(columnValue);
                  }
                } else {
                  // @ts-ignore
                  content = customerRender(columnValue);
                }
              }
              return (
                <Table.Summary.Cell key={column.key} className={tableSummaryBg} index={index + 1}>
                  {content}
                </Table.Summary.Cell>
              );
            }
          })}
        </Table.Summary.Row>
      );
    };
    return (
      <Table.Summary fixed="top">
        {renderRow(t('market.总计'), sumSummaryValue)}
        {renderRow(t('ViewDashboard.average'), toFixedAvgValue(avgSummaryValue), true)}
      </Table.Summary>
    );
  });

  const extraNode = useMemo(() => {
    return (
      <>
        <Space size={16}>
          <Button type="link" icon={<UnorderedListOutlined />} onClick={showRule}>
            {t('ViewDashboard.绩效考核计算规则')}
          </Button>
          {exportable && (
            <Button
              type="ghost"
              icon={<DownloadOutlined />}
              onClick={showExportModal}
              className="ant-radio-button-wrapper-checked"
            >
              {t('ViewDashboard.导出绩效表单')}
            </Button>
          )}
        </Space>
      </>
    );
  }, [exportable, showExportModal, showRule, t]);
  const [sortVisible, setSortVisible] = useState(false);

  const { DASHBOARD_TOKOPEDIA_TIPS2 } = useTriggerMapValue();
  const createTrigger = useCreateTrigger();

  const hasToko = useMemo(() => {
    return (
      departments?.some(({ platformStoreScopes }) => {
        return platformStoreScopes.some(({ platform, storeCount }) => {
          return platform === 'TOKOPEDIA' && storeCount > 0;
        });
      }) || false
    );
  }, [departments]);

  const [selectedRows, setSelectedRows] = useState<AssistantPerfRes[]>([]);
  const selectedRowKeys = useMemo(
    () => selectedRows.map(({ assistantId }) => assistantId),
    [selectedRows],
  );
  useEffect(() => {
    setSelectedRows([]);
  }, [dataSource]);

  return (
    <>
      <PerfSort
        setMeta={setMeta}
        sortedColumns={sortedColumns}
        visible={sortVisible}
        onClose={() => setSortVisible(false)}
      />
      <Head>
        <Space>
          {departmentSelect}
          {(departments || []).length > 0 && customSelect}
        </Space>
        <Space>{rangeSelect}</Space>
      </Head>
      {!DASHBOARD_TOKOPEDIA_TIPS2 && hasToko && (
        <Alert
          banner
          closable
          type="error"
          message={t('dashboard_tokopedia_tips4')}
          onClose={() => createTrigger({ trigger: 'DASHBOARD_TOKOPEDIA_TIPS2' })}
          style={{ marginBottom: 16 }}
        />
      )}
      <Container
        title={
          <div>
            {t('ViewDashboard.人工客服绩效统计')}
            {hasToko && (
              <Tooltip title={t('dashboard_tokopedia_tips4')}>
                <InfoCircleOutlined
                  style={{
                    marginLeft: 4,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
          </div>
        }
        extra={extraNode}
      >
        <Fold
          isfold={isfold}
          fixedCount={1}
          nodeList={[
            <Toolbar key="perf-table-toolbar">
              {dataSource?.length ? (
                <div>
                  <Checkbox
                    checked={selectedRows.length === dataSource.length}
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < dataSource.length
                    }
                    onChange={({ target: { checked } }) =>
                      setSelectedRows(checked ? dataSource : [])
                    }
                  >
                    {t('全选')}
                  </Checkbox>
                  <span>{t('aide.已选x项', { x: selectedRows.length })}</span>
                  <Button
                    type="link"
                    disabled={selectedRows.length === 0}
                    onClick={() => showExportAssistantModal(selectedRowKeys)}
                  >
                    {t('批量导出客服数据')}
                  </Button>
                </div>
              ) : (
                <div />
              )}
              <div>
                <Button
                  icon={<FoldIcon isfold={String(isfold)} />}
                  onClick={() => setIsfold((isfold) => !isfold)}
                >
                  {t('ViewDashboard.选择绩效考核项')}
                  {`(${selectedColumns.length - fixedColumns.length}/${
                    meta.length - fixedColumns.length
                  })`}
                </Button>
                <Button
                  onClick={() => setSortVisible(true)}
                  icon={<Icon component={SvgSort} />}
                  className="ml-[16px]"
                >
                  {t('todo.自定义展示方案')}
                </Button>
              </div>
            </Toolbar>,
            <SelectorWrapper key="perf-table-selector">
              <ColSelector meta={meta} setMeta={setMeta} />
            </SelectorWrapper>,
            <Placeholder />,
          ]}
        />
        {/* {mode.state === 'hasValue' && mode.contents === 'qa' ? (
        <Row>
          <Radio.Group
            value={filterCtx.conversationType}
            onChange={(e) => {
              onChange((pre) => ({ ...pre, conversationType: e.target.value }));
            }}
          >
            <Radio.Button value={1}>{t('ViewDashboard.人机结合')}</Radio.Button>
            <Radio.Button value={2}>{t('ViewDashboard.人工客服处理')}</Radio.Button>
          </Radio.Group>
        </Row>
      ) : null} */}

        {(departments || []).length > 0 && (
          <>
            <Table
              rowKey="assistantId"
              columns={columns}
              dataSource={dataSource}
              scroll={{ x: 'max-content', y: 550 }}
              summary={renderTableSummary}
              rowSelection={
                dataSource?.length
                  ? {
                      selectedRowKeys,
                      onChange: (_, rows) => setSelectedRows(rows),
                    }
                  : undefined
              }
            />
            <PerfRuleSelectorModal
              visible={ruleVisible}
              closable
              onCancel={hideRule}
              footer={null}
            />
          </>
        )}
      </Container>
      {exportable && exportModalVisible ? (
        <ExportPrefModal
          initialValue={{
            ...filterCtx,
          }}
          allColumns={meta}
          showColumns={selectedColumns}
          onClose={hideExportModal}
          sortedColumns={sortedColumns}
          fixedColumns={fixedColumns}
        />
      ) : null}
      {assistantIds?.length && exportAssistantModalVisible ? (
        <ExportAssistantModal
          initialValue={{
            ...filterCtx,
          }}
          assistantIds={assistantIds}
          onClose={() => closeExportAssistantModal()}
        />
      ) : null}
    </>
  );
};

export default PerfTable;

const Container = styled(Card)`
  border-top: none;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  color: var(--fett-text-primary);
  .ant-card-head {
    background: var(--fett-bg-panel);
    border-bottom-color: var(--fett-border-default);
    color: var(--fett-text-primary);
  }
  .ant-table {
    background: var(--fett-bg-panel);
    color: var(--fett-text-secondary);
  }
  .ant-table-thead > tr > th,
  .ant-table-thead > tr > td {
    color: var(--fett-text-primary);
    border-bottom-color: var(--fett-border-default);
    [data-color-mode='dark'] & {
      background: var(--fett-bg-elevated);
    }
  }
  .ant-table-tbody > tr > td {
    background: var(--fett-bg-panel);
    color: var(--fett-text-secondary);
    border-bottom-color: var(--fett-border-default);
  }
  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr > td.ant-table-cell-row-hover {
    background: var(--fett-bg-hover);
  }
  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right {
    [data-color-mode='dark'] & {
      background: var(--fett-bg-panel);
    }
  }
  .ant-table-thead > tr > .ant-table-cell-fix-left,
  .ant-table-thead > tr > .ant-table-cell-fix-right {
    [data-color-mode='dark'] & {
      background: var(--fett-bg-elevated);
    }
  }
  .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-left,
  .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-right {
    background: var(--fett-bg-hover);
  }
  .ant-table-summary {
    [data-color-mode='dark'] & {
      background: var(--fett-bg-panel);
    }
  }
  .ant-table-placeholder > td {
    background: var(--fett-bg-panel);
    border-bottom-color: var(--fett-border-default);
  }
  .ant-table-cell .ant-btn-link {
    color: var(--fett-app-primary-color);
  }

  .ant-card-body {
    padding-top: 16px;
    .ant-row {
      margin-bottom: 16px;
    }
    .ant-table {
      /* min-height: 600px; */
    }
  }
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: var(--fett-bg-panel);
  border-radius: 0 0 4px 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: var(--fett-text-secondary);
  .ant-select {
    min-width: 150px;
  }
  .ant-select .ant-select-selector {
    background-color: var(--fett-bg-input) !important;
    border-color: var(--fett-border-default) !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: var(--fett-text-secondary);
  }
  .ant-select-arrow,
  .ant-select-clear {
    color: var(--fett-text-tertiary);
  }
  .ant-picker {
    background-color: var(--fett-bg-input);
    border-color: var(--fett-border-default);
    color: var(--fett-text-secondary);
  }
  .ant-picker-suffix,
  .ant-picker-separator {
    color: var(--fett-text-tertiary);
  }
`;
const ColTitle = styled.div<{ width?: number }>`
  width: ${({ width }) => (width ? `${width}px` : 'initial')};
  svg {
    margin-left: 4px;
  }
`;
const Placeholder = styled.div`
  height: 24px;
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  > div {
    display: flex;
    align-items: center;
    > span {
      margin: 0 14px;
      color: var(--fett-text-tertiary);
    }
  }
`;

const SelectorWrapper = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  border: solid 1px var(--fett-border-default);
  background: var(--fett-bg-panel);
  border-radius: 4px;
  /* height: 400px; */
  overflow: auto;
`;

const FoldIcon = styled(DoubleRightOutlined)<{ isfold: string }>`
  transform: ${({ isfold }) => (isfold === 'true' ? 'rotateZ(90deg)' : 'rotateZ(-90deg)')};
  transition: all 0.3s !important;
`;

const tableSummaryBg = css`
  background-color: #fafafa;
  color: var(--fett-text-primary);
  [data-color-mode='dark'] & {
    background-color: var(--fett-bg-elevated);
  }
`;
